'use client';

import type React from 'react';
import { useTranslation } from '../../hooks/useTranslations';
import { EMAIL } from '../../constants/routes';
import './page.scss';
import Image from 'next/image';

export default function PrivacyPage() {
  const { t } = useTranslation();
  const renderWithEmail = (text: string) => {
    const parts = text.split(EMAIL);
    const result: Array<string | React.ReactNode> = [];
    parts.forEach((part, index) => {
      result.push(part);
      if (index < parts.length - 1) {
        result.push(
          <a
            key={`email-${index}`}
            href={`mailto:${EMAIL}`}
            className="privacy-page__email"
          >
            {EMAIL}
          </a>
        );
      }
    });
    return result;
  };

  const renderWithEmailAndHTML = (text: string) => {
    const emailReplaced = text.replace(EMAIL, `__EMAIL_PLACEHOLDER__`);
    const htmlString = emailReplaced.replace(
      /__EMAIL_PLACEHOLDER__/g,
      `<a href="mailto:${EMAIL}" class="privacy-page__email">${EMAIL}</a>`
    );
    return htmlString;
  };

  return (
    <>
      <div className="privacy-page">
        <div className="privacy-page__hero-section">
          <div className="privacy-background">
            <Image
              src="/privacy-bg.png"
              alt="Background"
              fill
              priority
              className="privacy-background__image"
            />
          </div>
          <div className="privacy-page__content">
            <div className="privacy-page__container">
              <h1 className="privacy-page__title">{t('privacy.title')}</h1>
              <p className="privacy-page__updated">{t('privacy.updated')}</p>

              <div className="privacy-page__text">
                <p dangerouslySetInnerHTML={{ __html: t('privacy.intro1') }} />
                <p>{t('privacy.intro2')}</p>

                <h2>{t('privacy.whoWeAre.title')}</h2>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('privacy.whoWeAre.content'),
                  }}
                />

                <h2>{t('privacy.informationWeCollect.title')}</h2>
                <p>{t('privacy.informationWeCollect.intro')}</p>

                <h3>{t('privacy.informationWeCollect.youProvide.title')}</h3>
                <ul>
                  {(
                    t('privacy.informationWeCollect.youProvide.items', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p>{t('privacy.informationWeCollect.youProvide.note')}</p>

                <h3>{t('privacy.informationWeCollect.weCollect.title')}</h3>
                <ul>
                  {(
                    t('privacy.informationWeCollect.weCollect.items', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h2>{t('privacy.howWeUse.title')}</h2>
                <p>{t('privacy.howWeUse.intro')}</p>
                <ul>
                  {(
                    t('privacy.howWeUse.items', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h2>{t('privacy.sharing.title')}</h2>
                <p>{t('privacy.sharing.intro')}</p>
                <ul>
                  {(
                    t('privacy.sharing.items', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li
                      key={index}
                      dangerouslySetInnerHTML={{ __html: item }}
                    />
                  ))}
                </ul>

                <h2>{t('privacy.rights.title')}</h2>
                <p>{t('privacy.rights.intro')}</p>
                <ul>
                  {(
                    t('privacy.rights.items', {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p>{renderWithEmail(t('privacy.rights.contact'))}</p>

                <h2>{t('privacy.retention.title')}</h2>
                <p>{t('privacy.retention.content')}</p>

                <h2>{t('privacy.cookies.title')}</h2>
                <p>{t('privacy.cookies.content')}</p>

                <h2>{t('privacy.security.title')}</h2>
                <p>{t('privacy.security.content')}</p>

                <h2>{t('privacy.children.title')}</h2>
                <p>{t('privacy.children.content')}</p>

                <h2>{t('privacy.transfers.title')}</h2>
                <p>{t('privacy.transfers.content')}</p>

                <h2>{t('privacy.updates.title')}</h2>
                <p>{t('privacy.updates.content')}</p>

                <h2>{t('privacy.contact.title')}</h2>
                <p
                  dangerouslySetInnerHTML={{
                    __html: renderWithEmailAndHTML(
                      t('privacy.contact.content')
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
